import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import RestoreSaveDialog from '../components/RestoreSaveDialog';
import EpisodeProgressDots from '../components/EpisodeProgressDots';
import GraphicNovelIntro from '../components/GraphicNovelIntro';
import gameApi, { type PlayerState, type ScenarioData, type StoryboardPage } from '../services/GameApiService';
import { REALTIME_MODEL } from '@selna/session';

import { saveSession, savePlayerEmail, type EpisodeSaveState } from '../utils/sessionPersistence';
import HandsFreeToggle from '../components/HandsFreeToggle';
import { useEpisodeReadyVoice } from '../voice/useEpisodeReadyVoice';
import { useHandsFree } from '../voice/HandsFreeProvider';
// 🔊 One-shot SFX (Fase 0 of the sound system plan, see
// docs/WIP/PLANO_SISTEMA_SOM.md). Coexists with voice TTS; the AudioContext
// is created lazily inside a user gesture.
import { useSfx } from '../audio';
import { useScrollToTopOnPhaseChange } from '../hooks/useScrollToTopOnPhaseChange';

// Shared session UI – provides the SAME conversation experience as Soft Skills
import {
  SessionServicesProvider,
  ScenarioSessionInner,
  VolumeCheckOverlay,
  type SessionServices,
  type SessionResults,
  type EmotionType,
  type ScenarioSessionProps,
} from '@selna/session-ui';

import { isAndroidChrome } from '@selna/session';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.selna.ai';

// ============================================================================
// Input mode persistence
// ============================================================================

const INPUT_MODE_STORAGE_KEY = 'selna_game_input_mode';
type InputMode = 'voice' | 'keyboard' | 'ptt';

function getSavedInputMode(): InputMode {
  try {
    const saved = localStorage.getItem(INPUT_MODE_STORAGE_KEY);
    if (saved === 'voice' || saved === 'keyboard' || saved === 'ptt') {
      return saved;
    }
  } catch {
    // localStorage unavailable (e.g. private browsing)
  }
  return 'ptt';
}

function saveInputMode(mode: InputMode): void {
  try {
    localStorage.setItem(INPUT_MODE_STORAGE_KEY, mode);
  } catch {
    // localStorage unavailable
  }
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Map game ScenarioData → ScenarioSessionInner's scenarioData prop.
 */
function mapToSessionScenarioData(
  scenario: ScenarioData,
): ScenarioSessionProps['scenarioData'] {
  if (!scenario.prompt) {
    throw new Error(
      `Scenario "${scenario.title}" has no prompt. This is a bug — all game scenarios must have a prompt generated by PromptService.`,
    );
  }

  return {
    id: `game_${Date.now()}`,
    isGameMode: true,
    title: scenario.title,
    description: scenario.frontendSituation || scenario.description,
    objectives: [],
    prompt: scenario.prompt,
    avatarImages: (scenario.avatarImages ?? {}) as Record<EmotionType, string>,
    voice: scenario.voice || 'ash', // From backend (AI-selected); fallback to male voice
    // Pass through extra fields so ScenarioSessionInner can use them
    characterName: scenario.characterName,
    frontendSituation: scenario.frontendSituation,
    aiRole: scenario.aiRole,
    userRole: scenario.userRole,
    characterDescription: scenario.characterDescription,
    // Investigation notebook: extracted facts from previous episodes
    userReferenceInfo: scenario.userReferenceInfo,
    // The game's firstSpeaker is always avatar (character greets first)
    firstSpeaker: 'avatar' as const,
  };
}

// ============================================================================
// Game Services
// ============================================================================

/**
 * Create game-specific service implementations for the shared session UI.
 *
 * @param magicLinkToken - The magic link JWT from the URL. Used as auth
 *   for all API calls (format: "Authorization: GameToken {jwt}").
 *
 * Real implementations:
 *   - analyzeEmotions: calls /api/realtime/analyze-emotions
 *   - validateTranscript: calls /api/transcript-validation
 *   - preloadAvatarImages: browser image preloader
 *   - webSocketConfig: auth via GameToken header
 *
 * No-ops (game doesn't need these yet):
 *   - guidedHints, detailedHelp, analysisMode, sessionTracking
 */
function createGameServices(magicLinkToken: string): SessionServices {
  const apiUrl = API_BASE_URL + '/api';

  /** Standard auth headers for all game API calls */
  const gameHeaders = (): Record<string, string> => ({
    'Content-Type': 'application/json',
    ...(magicLinkToken ? { Authorization: `GameToken ${magicLinkToken}` } : {}),
  });

  return {
    // ── Real implementations ─────────────────────────────────────────────

    // Emotion analysis – makes the avatar's face change to match conversation tone.
    // Uses dedicated game endpoint with reduced 10-emotion set.
    analyzeEmotions: async (request) => {
      try {
        const response = await fetch(`${apiUrl}/game/analyze-emotions`, {
          method: 'POST',
          headers: gameHeaders(),
          body: JSON.stringify(request),
        });

        if (!response.ok) {
          console.warn('[Game] Emotion analysis failed:', response.status);
          return { emotion: 'neutral' as EmotionType, confidence: 0 };
        }

        const result = await response.json();
        return {
          emotion: (result.emotion || 'neutral') as EmotionType,
          confidence: result.confidence ?? 0.8,
          reasoning: result.reasoning,
        };
      } catch (err) {
        console.warn('[Game] Emotion analysis error:', err);
        return { emotion: 'neutral' as EmotionType, confidence: 0 };
      }
    },

    // Transcript validation – detects poor audio quality so the user can restart.
    validateTranscript: async (scenarioId, scenarioTitle, recentExchanges, language) => {
      try {
        const response = await fetch(`${apiUrl}/transcript-validation`, {
          method: 'POST',
          headers: gameHeaders(),
          body: JSON.stringify({ scenarioId, scenarioTitle, recentExchanges, language }),
        });

        if (!response.ok) {
          // Don't interrupt the session if the validation endpoint itself fails
          return { success: false, valid: true, reason: '', conversationEnding: false, farewellSignals: '' };
        }

        return await response.json();
      } catch (err) {
        console.warn('[Game] Transcript validation error:', err);
        return { success: false, valid: true, reason: '', conversationEnding: false, farewellSignals: '' };
      }
    },

    // Avatar image preloader
    preloadAvatarImages: async (images, onProgress) => {
      const urls = Object.values(images).filter(Boolean);
      let loaded = 0;
      await Promise.all(
        urls.map(
          (url) =>
            new Promise<void>((resolve) => {
              const img = new Image();
              img.onload = img.onerror = () => {
                loaded++;
                onProgress?.(loaded, urls.length);
                resolve();
              };
              img.src = url;
            }),
        ),
      );
    },

    // ── No-ops (game doesn't use these yet) ──────────────────────────────

    // Guided hints – game has no hint system
    requestGuidedHint: async () => ({ shouldShowHint: false }),
    verifyHintFollowed: async () => ({ followed: false }),
    // Detailed help – game has no help system
    requestDetailedHelp: async () => ({ success: false }),
    // Session management – game provides pre-loaded scenarioData with prompt
    createSession: async () => {
      throw new Error('Game uses pre-loaded scenario data, createSession should not be called');
    },
    validateScenarioData: () => {},
    logCheckpoint: () => {},
    // Language – game is Portuguese-only for now
    getCurrentLanguage: () => 'pt-PT',
    // Game prompts are stored complete and 100% in Portuguese (behaviour
    // instructions are concatenated and translated in the backend). The
    // frontend just passes the prompt through without adding anything.
    combinePromptWithLanguage: (prompt: string) => prompt,
    // Analysis mode – not supported in game yet
    analysisUpdateScenario: async () => {
      throw new Error('Analysis mode not supported in game');
    },

    // ── WebSocket config ─────────────────────────────────────────────────
    // Auth via "GameToken {jwt}" header – validated by traineeOrScriptAuth
    // middleware on the /api/realtime-ws/token endpoint.
    webSocketConfig: {
      apiBaseUrl: apiUrl,
      credentials: null,
      createAuthHeaders: () => gameHeaders(),
      createSessionTracking: () => ({
        logMessage: () => {},
        logError: () => {},
      }),
    },

    // Config
    config: {
      realtimeModel: REALTIME_MODEL,
      defaultVoice: 'ash', // Fallback; actual voice comes from scenarioData.voice
      analysisModeTools: [],
    },
  };
}

// ============================================================================
// Component
// ============================================================================

type Status = 'loading' | 'intro' | 'ready' | 'completed' | 'error' | 'playing' | 'ending';

export default function EpisodePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [status, setStatus] = useState<Status>('loading');
  // Sempre que a fase visual muda (loading → ready → playing → ending → ready
  // após guardar, etc.) a página volta ao topo. Estas transições ocorrem dentro
  // da mesma rota (/play/:token), pelo que o ScrollToTop global não as cobre.
  useScrollToTopOnPhaseChange(status);
  const [error, setError] = useState('');
  const [playerState, setPlayerState] = useState<PlayerState | null>(null);
  const [scenarioData, setScenarioData] = useState<ScenarioData | null>(null);
  const [adventureTitle, setAdventureTitle] = useState('');
  const [totalEpisodes, setTotalEpisodes] = useState<number>(0);
  const [playerRole, setPlayerRole] = useState<string>('');
  const [initialCharacterAvatarUrl, setInitialCharacterAvatarUrl] = useState<string | null>(null);
  const [openingStoryboard, setOpeningStoryboard] = useState<StoryboardPage[] | null>(null);

  // 🎤 Persisted input mode preference (survives across episodes)
  const [preferredInputMode, setPreferredInputMode] = useState<InputMode>(getSavedInputMode);
  const handleInputModeChange = useCallback((mode: InputMode) => {
    saveInputMode(mode);
  }, []);

  // 🚗 Hands-free voice navigation — enabled lives in the app-level provider so
  // the AudioContext persists across navigation.
  const { enabled: handsFree, setEnabled: setHandsFreeEnabled } = useHandsFree();
  const handleHandsFreeChange = useCallback((next: boolean) => {
    setHandsFreeEnabled(next);
    // Turning hands-free ON also makes the upcoming conversation start in voice/VAD,
    // so the whole flow is operable without touching the screen.
    if (next) {
      saveInputMode('voice');
      setPreferredInputMode('voice');
    }
  }, [setHandsFreeEnabled]);

  // 🔊 SFX one-shots (Fase 0). `unlock` must be called inside a user gesture;
  // we call it from `startPlaying` (the "Começar Conversa" click). `play` is a
  // safe no-op when the manager hasn't been unlocked yet.
  const sfx = useSfx();

  // 🔊 Android Chrome Volume Check - prompt user to adjust media volume before getUserMedia
  const [showVolumeCheck, setShowVolumeCheck] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  // 💾 Save/Restore state
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [savedEpisodeState, setSavedEpisodeState] = useState<EpisodeSaveState | null>(null);
  const [initialTranscript, setInitialTranscript] = useState<Array<{ text: string; timestamp: number; speaker: 'user' | 'ai'; id?: string }> | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isCheckingSave, setIsCheckingSave] = useState(false);

  // 🔙 Back button / exit confirmation state
  const [showExitDialog, setShowExitDialog] = useState(false);

  const startTimeRef = useRef<number>(0);
  const allowUnloadForRestartRef = useRef(false);

  // Game services for shared session UI (stable across renders)
  // Pass the magic link token so API calls are authenticated via "GameToken {jwt}"
  const gameServices = useMemo(() => createGameServices(token || ''), [token]);

  // =========================================================================
  // 🔙 Browser back button interception during active session
  // =========================================================================

  useEffect(() => {
    if (status !== 'playing') return;

    // Push a dummy history entry so pressing "back" triggers popstate
    // instead of actually navigating away
    window.history.pushState({ episodeGuard: true }, '');

    const handlePopState = () => {
      // Show exit confirmation dialog instead of navigating away
      setShowExitDialog(true);
      // Re-push the guard entry so the next back press is also intercepted
      window.history.pushState({ episodeGuard: true }, '');
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [status]);

  // 🔙 Prevent accidental tab/browser close during active session
  useEffect(() => {
    if (status !== 'playing') return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (allowUnloadForRestartRef.current) return;
      e.preventDefault();
      // Modern browsers show a generic message; setting returnValue is required for some
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [status]);

  // Restart scenario from poor-audio overlay without triggering "leave site?" dialog
  const handleRestartScenario = useCallback(() => {
    allowUnloadForRestartRef.current = true;
    window.location.reload();
  }, []);

  // =========================================================================
  // Data loading
  // =========================================================================

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    loadEpisodeData();
  }, [token]);

  const loadEpisodeData = async () => {
    if (!token) return;

    try {
      const data = await gameApi.getEpisodeData(token);

      if (!data.adventureTitle) {
        throw new Error('Faltam dados da aventura');
      }
      if (typeof data.totalEpisodes !== 'number' || data.totalEpisodes < 1) {
        throw new Error('Faltam dados do total de episódios');
      }
      if (!data.playerRole) {
        throw new Error('Faltam dados do papel do jogador');
      }

      setPlayerState(data.playerState);
      setAdventureTitle(data.adventureTitle);
      setTotalEpisodes(data.totalEpisodes);
      setPlayerRole(data.playerRole);
      setInitialCharacterAvatarUrl(data.initialCharacterAvatarUrl ?? null);

      saveSession({
        token: token!,
        adventureTitle: data.adventureTitle,
        currentEpisode: data.playerState.currentEpisode,
        totalEpisodes: data.totalEpisodes,
        lastPage: 'play',
        playerStatus: data.playerState.status,
      });
      if (data.playerState.email) {
        savePlayerEmail(data.playerState.email);
      }

      if (data.status === 'completed') {
        // Redirect to the completion page with full evaluation
        navigate(`/complete/${token}`);
        return;
      } else if (data.status === 'waiting') {
        navigate(`/waiting/${token}`);
      } else if (data.status === 'ready') {
        if (!data.scenarioData) {
          throw new Error('Faltam dados do cenário para iniciar o episódio');
        }
        setScenarioData(data.scenarioData);

        // Check for saved episode state
        const hasSavedState = !!(data.savedEpisodeState && data.savedEpisodeState.transcript && data.savedEpisodeState.transcript.length > 0);
        if (hasSavedState) {
          setSavedEpisodeState(data.savedEpisodeState!);
          setShowRestoreDialog(true);
        }

        // Spoiler-free graphic-novel intro: only before episode 1, only when all
        // 4 images exist, and never when resuming a saved mid-episode state.
        const storyboard = data.openingStoryboard ?? null;
        const showIntro = data.playerState.currentEpisode === 1
          && !hasSavedState
          && Array.isArray(storyboard)
          && storyboard.length === 4
          && storyboard.every(p => p && p.imageUrl && p.text);
        setOpeningStoryboard(showIntro ? storyboard : null);
        setStatus(showIntro ? 'intro' : 'ready');
      }
      setRetryCount(0); // Reset retry count on success
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load episode';
      
      // If it's a "Failed to fetch" error and we haven't exceeded retries, try again
      if (errorMessage.includes('Failed to fetch') && retryCount < maxRetries) {
        console.log(`[EpisodePage] Network error, retry ${retryCount + 1}/${maxRetries}`);
        setRetryCount(retryCount + 1);
        // Retry after a delay (exponential backoff: 1s, 2s, 4s)
        setTimeout(() => {
          loadEpisodeData();
        }, Math.pow(2, retryCount) * 1000);
      } else {
        // Either not a network error, or we've exceeded retries
        setError(errorMessage);
        setStatus('error');
        // 🔊 Initial load failed permanently — only sound once we give up.
        sfx.play('error');
      }
    }
  };

  // =========================================================================
  // Session callbacks
  // =========================================================================

  const startPlaying = async () => {
    // 🔊 Unlock the SFX AudioContext from this user gesture. The click on
    // "Começar Conversa" is the gesture — we capture it here before any
    // `await` so the context is allowed to resume. The actual `play()`
    // call is sync and stays inside the gesture.
    void sfx.unlock();
    sfx.play('episode_start');

    // Re-check server for saved state — the local cache may be stale
    // (e.g. user saved on another tab/device, or page was loaded before the save)
    if (!savedEpisodeState && token) {
      setIsCheckingSave(true);
      try {
        const freshData = await gameApi.getEpisodeData(token);
        if (freshData.savedEpisodeState && freshData.savedEpisodeState.transcript && freshData.savedEpisodeState.transcript.length > 0) {
          setSavedEpisodeState(freshData.savedEpisodeState);
          setIsCheckingSave(false);
          setShowRestoreDialog(true);
          return;
        }
      } catch (err) {
        console.warn('[EpisodePage] Failed to re-check save state, proceeding without:', err);
      }
      setIsCheckingSave(false);
    }

    if (savedEpisodeState && savedEpisodeState.transcript && savedEpisodeState.transcript.length > 0) {
      setShowRestoreDialog(true);
      return;
    }

    if (isAndroidChrome()) {
      setShowVolumeCheck(true);
    } else {
      startTimeRef.current = Date.now();
      setStatus('playing');
    }
  };

  // 🚗 Hands-free voice on the ready screen: read situation aloud, listen for "começar".
  // enabled is false the moment we leave 'ready' (or a restore/volume dialog is up),
  // which tears the navigator down before the conversation's audio session mounts.
  useEpisodeReadyVoice({
    enabled: handsFree && status === 'ready' && !showRestoreDialog && !showVolumeCheck,
    token: token || '',
    scenarioData,
    playerRole,
    // The situation/opener is only read on episode 1; from episode 2 on it is the
    // bridge, read on the waiting screen.
    isFirstEpisode: playerState?.currentEpisode === 1,
    onStart: startPlaying,
  });

  const handleVolumeCheckContinue = () => {
    setShowVolumeCheck(false);
    startTimeRef.current = Date.now();
    setStatus('playing');
  };

  const handleVolumeCheckBack = () => {
    setShowVolumeCheck(false);
  };

  // 💾 Save game state for later resumption
  const handleSaveGame = useCallback(async (results: SessionResults) => {
    if (!token || !scenarioData || !playerState) return;

    setIsSaving(true);
    try {
      const saveState: EpisodeSaveState = {
        odysseyId: playerState.id,
        episodeNumber: playerState.currentEpisode,
        scenarioId: null,
        transcript: results.transcript.map(m => ({
          timestamp: m.timestamp,
          speaker: m.speaker,
          text: m.text,
        })),
        emotionHistory: (results.emotionHistory || []).map(e => ({
          timestamp: e.timestamp,
          emotion: e.emotion as string,
        })),
        duration: Date.now() - (startTimeRef.current || Date.now()),
        turnCount: results.transcript.filter(m => m.speaker === 'user').length,
        scenarioData: {
          title: scenarioData.title,
          aiRole: scenarioData.aiRole,
          userRole: scenarioData.userRole,
          characterName: scenarioData.characterName,
          prompt: scenarioData.prompt,
          voice: scenarioData.voice,
        },
        savedAt: new Date().toISOString(),
        version: 1,
      };

      await gameApi.saveEpisodeState(token, saveState);
      console.log('[EpisodePage] Game state saved successfully');

      // Update local state so the ready screen knows there's a save
      setSavedEpisodeState(saveState);
      setInitialTranscript(undefined); // Reset so it's not auto-loaded
      setShowRestoreDialog(true); // Show restore dialog when returning to ready
      setShowExitDialog(false); // Dismiss exit dialog if open

      // Navigate back to the ready screen
      setStatus('ready');
      // 🔊 Save confirmed — short success pluck.
      sfx.play('success');
    } catch (err) {
      console.error('[EpisodePage] Failed to save game state:', err);
      // 🔊 Save failed — low buzzer (no-op if muted / not yet unlocked).
      sfx.play('error');
      throw err; // Re-throw so the UI can show error
    } finally {
      setIsSaving(false);
    }
  }, [token, scenarioData, playerState, sfx]);

  // 💾 Handle restore from save dialog
  const handleRestore = useCallback(() => {
    if (!savedEpisodeState) return;
    
    // Set the initial transcript for the session
    setInitialTranscript(savedEpisodeState.transcript);
    setShowRestoreDialog(false);
    
    // Start playing with the restored transcript
    startTimeRef.current = Date.now() - (savedEpisodeState.duration || 0);
    setStatus('playing');
  }, [savedEpisodeState]);

  // 💾 Handle start fresh (discard save)
  const handleStartFresh = useCallback(async () => {
    if (!token) return;
    
    setIsClearing(true);
    try {
      await gameApi.clearSavedEpisodeState(token);
      setSavedEpisodeState(null);
      setInitialTranscript(undefined);
      setShowRestoreDialog(false);
      
      // Start playing immediately after clearing the save
      if (isAndroidChrome()) {
        setShowVolumeCheck(true);
      } else {
        startTimeRef.current = Date.now();
        setStatus('playing');
      }
    } catch (err) {
      console.error('[EpisodePage] Failed to clear saved state:', err);
    } finally {
      setIsClearing(false);
    }
  }, [token]);

  const handleEpisodeComplete = useCallback(
    async (results: SessionResults) => {
      if (!token) return;

      // Show loading state immediately so user gets feedback
      setStatus('ending');
      // 🔊 Episode finished — bell-like two-note resolution.
      sfx.play('episode_end');

      // Build plain-text transcript for the backend
      const transcript = results.transcript
        .map(
          (m) =>
            `${m.speaker === 'user' ? 'Utilizador' : scenarioData?.characterName || scenarioData?.aiRole || 'AI'}: ${m.text}`,
        )
        .join('\n\n');

      const duration = Math.round((Date.now() - startTimeRef.current) / 1000);
      const turnCount = results.transcript.filter((m) => m.speaker === 'user').length;

      try {
        const result = await gameApi.completeEpisode(token, transcript, {
          duration,
          turnCount,
        });

        navigate(`/waiting/${result.waitingToken}`, {
          state: {
            episodeSummary: result.episodeSummary,
            characterName: scenarioData?.characterName || scenarioData?.aiRole || '',
            episodeNumber: playerState?.currentEpisode ?? 1,
            isLastEpisode: result.isLastEpisode || false,
            isResolveEpisodeCompleted: result.isResolveEpisodeCompleted || false,
            totalEpisodes: result.totalEpisodes,
            avatarUrl: scenarioData?.avatarThumbUrl ?? scenarioData?.avatarImages?.neutral ?? null,
          },
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to complete episode');
        setStatus('error');
        // 🔊 Could not finalize the episode on the server.
        sfx.play('error');
      }
    },
    [token, scenarioData, navigate, sfx],
  );

  // =========================================================================
  // Mapped scenario data for ScenarioSessionInner
  // =========================================================================

  const mappedScenarioData = useMemo(
    () => (scenarioData ? mapToSessionScenarioData(scenarioData) : undefined),
    [scenarioData],
  );

  // =========================================================================
  // Render – game-specific screens
  // =========================================================================

  // Loading
  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" message="A carregar episódio..." />
      </div>
    );
  }

  // Opening storyboard – graphic-novel intro before the first conversation
  if (status === 'intro' && openingStoryboard) {
    return (
      <GraphicNovelIntro
        pages={openingStoryboard}
        onComplete={() => setStatus('ready')}
      />
    );
  }

  // Error
  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-2xl font-bold text-dark mb-4">Oops! Algo correu mal</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <a
            href="/"
            className="inline-block px-6 py-3 bg-primary text-white rounded-xl hover:bg-opacity-90 transition-colors"
          >
            Voltar ao início
          </a>
        </div>
      </div>
    );
  }

  // Completed
  if (status === 'completed') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-3xl font-bold text-dark mb-4">Aventura Completa!</h1>
          <p className="text-lg text-gray-600 mb-6">
            Parabéns! Completaste a aventura "{adventureTitle}".
          </p>
          {playerState && (
            <p className="text-gray-500 mb-8">
              {playerState.completedEpisodes.length} episódios concluídos
            </p>
          )}
          <a
            href="/"
            className="inline-block px-6 py-3 bg-primary text-white rounded-xl hover:bg-opacity-90 transition-colors"
          >
            Nova Aventura
          </a>
        </div>
      </div>
    );
  }

  // Ending – episode is being processed
  if (status === 'ending') {
    return (
      <div className="dark min-h-screen flex items-center justify-center px-4 bg-dark">
        <div className="max-w-md w-full text-center">
          <LoadingSpinner size="lg" message="A processar o episódio..." />
          <p className="text-gray-500 dark:text-white/70 mt-4 text-sm">
            Estamos a preparar o resumo da tua conversa.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'ready' && scenarioData) {
    // 💾 Restore Save Dialog
    if (showRestoreDialog && savedEpisodeState) {
      return (
        <RestoreSaveDialog
          savedState={savedEpisodeState}
          onRestore={handleRestore}
          onStartFresh={handleStartFresh}
          isClearing={isClearing}
        />
      );
    }

    // 🔊 Android Chrome Volume Check Overlay
    if (showVolumeCheck) {
      return (
        <VolumeCheckOverlay
          onContinue={handleVolumeCheckContinue}
          onBack={handleVolumeCheckBack}
        />
      );
    }

    const heroImageUrl = scenarioData.avatarImages?.neutral ?? null;

    return (
      <div className="relative min-h-screen overflow-hidden">
        {/* Cinematic background. The character's portrait — blurred and heavily
            darkened — carries the opening storyboard's visual language into the
            episode opener instead of dropping the player into a flat form.
            Fixed so the atmosphere stays put while the prose scrolls; falls back
            to a plain dark gradient when no portrait is available. */}
        {heroImageUrl && (
          <img
            src={heroImageUrl}
            alt=""
            aria-hidden
            className="fixed inset-0 w-full h-full object-cover scale-110 blur-[3px]"
            draggable={false}
          />
        )}
        <div className="fixed inset-0 bg-gradient-to-b from-black/85 via-black/75 to-black/90" />

        <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
          <div className="max-w-2xl w-full">
            {/* Hands-free voice toggle */}
            <div className="flex justify-end mb-4">
              <HandsFreeToggle enabled={handsFree} onChange={handleHandsFreeChange} />
            </div>
            {/* Header */}
            <div className="text-center mb-8 [text-shadow:0_2px_12px_rgba(0,0,0,0.6)]">
              <span className="text-sm font-medium text-primary uppercase tracking-wider">
                {adventureTitle}
              </span>
              {totalEpisodes > 0 && playerState && (
                <p className="text-sm text-white/70 mt-1">
                  Episódio {playerState.currentEpisode} de {totalEpisodes}
                </p>
              )}
              <h1 className="text-3xl md:text-4xl font-bold text-white mt-2">
                {scenarioData.title}
              </h1>
              {/* Episode progress dots */}
              {totalEpisodes > 0 && playerState && (
                <EpisodeProgressDots
                  totalEpisodes={totalEpisodes}
                  currentEpisode={playerState.currentEpisode}
                  initialCharacterAvatarUrl={initialCharacterAvatarUrl}
                  completedEpisodes={playerState.completedEpisodes.map(ep => ({
                    episodeNumber: ep.episodeNumber,
                    avatarUrl: ep.avatarUrl ?? null,
                  }))}
                  currentEpisodeAvatarUrl={scenarioData.avatarThumbUrl ?? scenarioData.avatarImages?.neutral ?? null}
                  ringOffsetClassName="ring-offset-black"
                />
              )}
            </div>

            {/* Character with Avatar — frosted card */}
            <div className="mb-6 p-4 bg-white/10 backdrop-blur-md border border-white/15 rounded-xl">
              <div className="flex items-start gap-4">
                {scenarioData.avatarImages?.neutral && (
                  <div className="flex-shrink-0">
                    <img
                      src={scenarioData.avatarThumbUrl ?? scenarioData.avatarImages.neutral}
                      alt={scenarioData.aiRole}
                      className="w-20 h-20 rounded-full object-cover border-2 border-white/50 shadow-md"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-white/60 uppercase mb-1">
                    Vais falar com
                  </h3>
                  <p className="text-lg font-semibold text-white">
                    {scenarioData.characterName && scenarioData.aiRole
                      ? `${scenarioData.characterName} - ${scenarioData.aiRole}`
                      : scenarioData.characterName || scenarioData.aiRole}
                  </p>
                  {(scenarioData.playerFacingDescription ||
                    scenarioData.characterDescription) && (
                    <p className="text-white/70 text-sm mt-1">
                      {scenarioData.playerFacingDescription ||
                        scenarioData.characterDescription}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Your Role — frosted card */}
            <div className="mb-8 p-4 bg-white/10 backdrop-blur-md border border-white/15 rounded-xl">
              <h3 className="text-sm font-medium text-primary uppercase mb-1">O teu papel</h3>
              <p className="text-white">{playerRole || scenarioData.userRole}</p>
            </div>

            {/* Start Button */}
            <div className="text-center">
              <button
                onClick={startPlaying}
                disabled={isCheckingSave}
                className="px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-primary to-accent rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-70 disabled:cursor-wait disabled:transform-none"
              >
                {isCheckingSave ? 'A preparar...' : 'Começar Conversa'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // =========================================================================
  // Playing – delegate to the shared ScenarioSessionInner
  // =========================================================================
  if (status === 'playing' && mappedScenarioData) {
    return (
      <SessionServicesProvider services={gameServices}>
        <ScenarioSessionInner
          scenarioData={mappedScenarioData}
          onFinish={handleEpisodeComplete}
          onCancel={() => {
            setInitialTranscript(undefined);
            setShowExitDialog(false);
            setStatus('ready');
          }}
          skipIntro={true}
          initialInputMode={preferredInputMode}
          onInputModeChange={handleInputModeChange}
          allowKeyboardInput={true}
          initialTranscript={initialTranscript}
          onSave={handleSaveGame}
          isSaving={isSaving}
          showExitConfirmation={showExitDialog}
          onExitConfirmationDismiss={() => setShowExitDialog(false)}
          onRequestExitConfirmation={() => setShowExitDialog(true)}
          onRestartScenario={handleRestartScenario}
        />
      </SessionServicesProvider>
    );
  }

  return null;
}
